// Nura Health blog post data.
// Internal links use the {LINK:link text|/path} marker syntax.
// BlogPost.jsx parses these and renders them as React Router <Link> components.
//
// HERO IMAGE PLACEHOLDERS — no heroImage/heroSrc field exists in the renderer yet.
// When images are ready, add a heroImage field to each post and update BlogPost.jsx
// to render it. Each post below has a comment indicating the intended hero shot.

export const blogPosts = [

  // ─────────────────────────────────────────────────────────────────────────────
  // POST 1 — at-home-blood-collection-brisbane-guide
  // HERO IMAGE PLACEHOLDER: phlebotomist in branded uniform being welcomed at a
  // suburban Brisbane front door by a smiling resident, warm natural daylight.
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'at-home-blood-collection-brisbane-guide',
    title: 'At-Home Blood Collection: The Complete Guide',
    metaTitle: 'At-Home Blood Collection: The Complete Guide | Nura Health',
    metaDescription:
      'Everything you need to know about at-home blood collection — what it is, who can use it, which tests are covered, and how to book a qualified mobile phlebotomist.',
    date: '2026-05-25',
    author: 'Nura Health',
    readTime: '9 min read',
    heroAlt:
      'Qualified mobile phlebotomist arriving at a residential home for an at-home blood collection appointment',
    heroImage: '/images/blog/at-home-collection-hero.jpg',
    excerpt:
      'Everything you need to know about at-home blood collection — what it is, who can use it, which tests are covered, and exactly how to book a qualified mobile phlebotomist.',
    content: [
      {
        type: 'h2',
        text: 'The waiting room you never have to sit in',
      },
      {
        type: 'p',
        text: 'For most people, a blood test means the same routine: book a slot you may or may not get, drive across town, find parking, take a ticket, and wait in a crowded collection centre — sometimes while fasting, sometimes while unwell, sometimes with a restless child or an elderly parent in tow. For a surprising number of people, that journey is the single biggest reason a doctor-ordered test gets delayed for weeks.',
      },
      {
        type: 'p',
        text: 'At-home blood collection removes the journey entirely. A qualified phlebotomist comes to your front door, draws your blood in your own kitchen or living room, and transports your sample to an accredited laboratory for testing. You never leave the house. This guide walks through exactly how it works, who it\'s for, what it can and can\'t do, and how to arrange a visit.',
      },
      {
        type: 'h2',
        text: 'What "at-home blood collection" actually means',
      },
      {
        type: 'p',
        text: 'At-home blood collection — also called mobile pathology or domiciliary collection — is a professional service where a trained, certified collector travels to wherever you are to take a blood sample for testing. It is the same venepuncture procedure performed in any collection centre, carried out by a person who holds the same qualifications, using the same sterile equipment and the same specimen tubes.',
      },
      {
        type: 'p',
        text: 'The important distinction: this is not a do-it-yourself kit. It is a clinical service delivered by a professional. The collector follows accredited protocols for patient identification, sample labelling, and chain of custody, then delivers your specimens to a partner laboratory under temperature-controlled, regulation-compliant transport. Your results flow back to your referring doctor in exactly the same way they would from a centre visit.',
      },
      {
        type: 'p',
        text: 'People sometimes ask whether blood can really be "given" at home. It\'s worth separating three things that the phrase can mean. Having a sample drawn for pathology testing — the subject of this guide — is widely available across Brisbane, Sydney and Melbourne. Receiving certain treatments like injections or infusions at home is possible but requires a specialist clinical service. Donating blood for the blood bank, on the other hand, must be done at a designated donor centre. When most people search for blood collection at home, it\'s the first one they\'re after — and that\'s the routine, accessible service Nura Health provides.',
      },
      {
        type: 'h2',
        text: 'Who at-home collection is for',
      },
      {
        type: 'p',
        text: 'The honest answer is: almost anyone with a valid pathology request form from their doctor. But it makes the biggest difference for certain groups.',
      },
      {
        type: 'p',
        text: 'People with mobility challenges or chronic illness, for whom leaving the house is genuinely difficult, benefit enormously. So do elderly residents, who may find a centre visit exhausting or disorienting. Parents of young children avoid the ordeal of managing a fasting toddler in a waiting room. People with needle anxiety often find a calm, familiar home environment far less stressful than a clinical setting. Shift workers, carers, and time-poor professionals simply value getting hours of their day back.',
      },
      {
        type: 'p',
        text: 'There are also two groups for whom at-home collection isn\'t just convenient but removes a real structural barrier: NDIS participants and aged care residents. We\'ve written about both in detail in a {LINK:dedicated guide for NDIS participants and aged care facilities|/blog/mobile-pathology-ndis-aged-care}, because the benefits there extend well beyond convenience into cost and compliance.',
      },
      {
        type: 'h2',
        text: 'What blood tests can be done at home',
      },
      {
        type: 'p',
        text: 'This is one of the most common questions, and the answer is reassuring: almost any routine blood test your GP or specialist can order can be collected at home. That includes full blood count (FBC), thyroid function (TSH, T3, T4), HbA1c for diabetes monitoring, lipid and cholesterol panels, liver and kidney function, iron studies, vitamin D and B12, hormone panels, inflammatory markers, and the great majority of standard pathology requests.',
      },
      {
        type: 'p',
        text: 'Fasting tests — such as fasting glucose or a fasting lipid profile — are entirely possible at home, and in many ways easier, because there\'s no drive to the centre tempting you to break your fast. There are a small number of specialised tests that require immediate laboratory processing or special handling, and those occasionally still need a centre. A good mobile provider will tell you upfront if anything on your request form falls into that category, rather than turning up and finding out.',
      },
      {
        type: 'h2',
        text: 'How the process works, step by step',
      },
      {
        type: 'p',
        text: 'The process is genuinely straightforward. First, you see your doctor and receive a pathology request form — this is the referral that tells the lab which tests to run, and it\'s required for any legitimate collection, at home or at a centre. Second, you book a visit, choosing a time that suits you. Third, your collector arrives at the agreed time, confirms your identity carefully against your request form, and performs the draw. Fourth, your labelled samples are transported to the accredited partner laboratory. Finally, your results are sent back to your referring doctor, who discusses them with you.',
      },
      {
        type: 'p',
        text: 'From your side, the whole appointment usually takes ten to fifteen minutes. The collector handles everything else.',
      },
      {
        type: 'h2',
        text: 'Is it available across Brisbane, Sydney and Melbourne metros?',
      },
      {
        type: 'p',
        text: 'Yes. Mobile pathology is well established across Australia. Nura Health operates across Brisbane, Sydney and Melbourne metros, with certified collectors travelling to homes, aged care facilities, and workplaces throughout each region. Coverage and same-day availability depend on location and demand, so the best approach is simply to ask when you book.',
      },
      {
        type: 'h2',
        text: 'What it costs',
      },
      {
        type: 'p',
        text: 'A blood test taken at a collection centre is typically bulk-billed and free with Medicare. At-home collection adds a service fee for the convenience of the visit — for most patients this sits in the $55–$80 range depending on whether it\'s a standard or same-day request. We\'ve broken down every pricing scenario, including NDIS and aged care arrangements, in our {LINK:dedicated guide to blood test costs and preparation|/blog/blood-test-cost-and-preparation-australia}.',
      },
      {
        type: 'h2',
        text: 'The bottom line',
      },
      {
        type: 'p',
        text: 'At-home blood collection takes a task that costs most people half a morning and turns it into a ten-minute visit in their own home, performed to exactly the same clinical standard as a centre. If you have a request form from your doctor and you\'d rather not make the trip, it\'s very likely an option for you.',
      },
      {
        type: 'p',
        text: '{LINK:Book with Nura Health|/booking} to arrange a visit from a qualified mobile collector across Brisbane, Sydney and Melbourne metros.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // POST 2 — at-home-blood-collection-safe-accurate
  // HERO IMAGE PLACEHOLDER: clean close-up of gloved professional hands with
  // sterile, sealed single-use phlebotomy equipment laid out neatly on a surface.
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'at-home-blood-collection-safe-accurate',
    title: 'Is At-Home Blood Collection Safe and Accurate? What Every Patient Should Know',
    metaTitle: 'Is At-Home Blood Collection Safe and Accurate? | Nura Health',
    metaDescription:
      'When performed by a qualified mobile phlebotomist, at-home blood tests are exactly as safe and accurate as a collection centre. Here\'s what actually protects you — and how to tell a professional service from a risky DIY kit.',
    date: '2026-05-25',
    author: 'Nura Health',
    readTime: '8 min read',
    heroAlt:
      'Sterile single-use phlebotomy equipment laid out on a clean surface, ready for professional at-home blood collection',
    heroImage: '/images/blog/safe-accurate-hero.jpg',
    excerpt:
      'When performed by a qualified mobile phlebotomist, at-home blood tests are exactly as safe and accurate as a collection centre. Here\'s what actually protects you — and how to tell a professional service from a risky DIY kit.',
    content: [
      {
        type: 'h2',
        text: 'The question behind the question',
      },
      {
        type: 'p',
        text: 'When people ask whether at-home blood collection is safe or accurate, what they\'re really asking is: "If I\'m not in a clinic, am I giving something up?" It\'s a fair concern. We\'ve been conditioned to associate medical procedures with clinical rooms, and a kitchen table can feel like a downgrade.',
      },
      {
        type: 'p',
        text: 'The reassuring reality is that the safety and accuracy of a blood test have almost nothing to do with the room it\'s taken in, and almost everything to do with who takes it and how. A qualified collector drawing blood in your living room follows the same protocols, uses the same equipment, and produces a sample of the same quality as one drawn in any collection centre. This article explains exactly what protects you — and, just as importantly, what to watch out for.',
      },
      {
        type: 'h2',
        text: 'Accuracy: it\'s about the collector and the lab, not the location',
      },
      {
        type: 'p',
        text: 'A blood test result is only as good as two things: the integrity of the sample and the laboratory that analyses it. Neither depends on geography.',
      },
      {
        type: 'p',
        text: 'Sample integrity comes down to correct technique — the right tubes, in the right order, filled to the right level, gently mixed, labelled accurately, and transported under the right conditions within the right timeframe. A trained phlebotomist does all of this identically whether they\'re in a hospital, a centre, or your home. The analysis itself happens at the same accredited laboratories that process centre-collected samples. In other words, a professionally collected home sample and a centre sample arrive at the same lab, run on the same machines, and produce results of the same reliability.',
      },
      {
        type: 'p',
        text: 'This is the crucial line to draw: professional mobile collection is as accurate as centre collection. Consumer DIY finger-prick kits are a different thing entirely, and we\'ll come back to them.',
      },
      {
        type: 'h2',
        text: 'Safety: the risks are minimal, and the same as anywhere',
      },
      {
        type: 'p',
        text: 'Venepuncture is among the most routine procedures in healthcare. When performed by a qualified collector, the risks are minor and uncommon: a little bruising, brief lightheadedness, or rarely a small amount of localised soreness. Serious complications are very rare. None of this changes between a clinic and a home — what keeps it safe is the collector\'s training, sterile single-use equipment, and proper technique, all of which travel with them.',
      },
      {
        type: 'p',
        text: 'If anything, a home environment can make the experience safer for some patients. Someone prone to fainting is in a familiar, comfortable space. An anxious patient is calmer. An elderly or frail person isn\'t exhausted by travel before the needle even comes out.',
      },
      {
        type: 'h2',
        text: 'What actually protects you: three professional safeguards',
      },
      {
        type: 'p',
        text: 'There are three things that separate a safe, accurate service from a risky one. Knowing them lets you judge any provider.',
      },
      {
        type: 'p',
        text: 'The first is qualification. A legitimate mobile collector holds a Certificate III in Pathology Collection (HLT37215) — the nationally recognised qualification for the role — and works under accredited protocols. They should also carry professional indemnity insurance, hold a current police check, and where relevant a Blue Card. Nura\'s collectors meet all of these as standard.',
      },
      {
        type: 'p',
        text: 'The second is correct patient identification. This brings us to the single biggest mistake any phlebotomist can make, anywhere: drawing or labelling a sample for the wrong patient. Misidentification is the error that can genuinely cause harm, because it attaches one person\'s results to another\'s name. Every professional collection follows a strict identity-confirmation step — checking your full name, date of birth, and details against your request form before a single tube is filled. If a collector ever skips this, that\'s a red flag regardless of where you are.',
      },
      {
        type: 'p',
        text: 'The third is knowing when to stop. There\'s a widely followed principle in phlebotomy often called the "two-stick rule": a collector attempts a draw no more than twice before stopping and reassessing rather than repeatedly probing. It exists to protect you from unnecessary discomfort and bruising when a vein is difficult. A professional collector respects it; they\'ll pause, try a different approach, or arrange an alternative rather than persisting. It\'s a quiet marker of someone who puts your comfort first.',
      },
      {
        type: 'h2',
        text: 'Why you should never draw blood yourself',
      },
      {
        type: 'p',
        text: 'It\'s worth being direct about this, because people do search for how to draw their own blood. Don\'t. Venepuncture performed without training carries real risks — nerve injury, damage to the vein, infection from improper technique, fainting without anyone present, and samples that are unusable because they were collected or handled incorrectly. The procedure looks simple and isn\'t. The entire point of a mobile service is that you get the convenience of "at home" without taking on any of the risk: a trained professional does the part that requires training.',
      },
      {
        type: 'h2',
        text: 'Where DIY kits fit (and where they don\'t)',
      },
      {
        type: 'p',
        text: 'At-home blood collection kits — the consumer finger-prick products you can buy online — are a separate category from professional mobile pathology, and confusing the two is where people go wrong. A finger-prick kit collects a few drops of capillary blood for a limited range of screening tests, and the accuracy depends heavily on whether you followed the instructions perfectly. They have a place for some basic screening. They are not a substitute for a doctor-ordered pathology panel drawn by venepuncture, and they shouldn\'t be used to make or monitor a serious clinical decision. When your GP orders a test, what you want is professional collection — not a kit.',
      },
      {
        type: 'h2',
        text: 'The bottom line',
      },
      {
        type: 'p',
        text: 'At-home blood collection, done by a qualified mobile phlebotomist, is exactly as safe and accurate as walking into a collection centre — same training, same equipment, same accredited labs, same results. What matters is the professional, not the place. Check that your collector is qualified, insured, and rigorous about identifying you correctly, and you lose nothing by staying home.',
      },
      {
        type: 'p',
        text: 'Every Nura Health collector is Certificate III qualified, fully insured, and police-checked. {LINK:Read the complete guide to at-home blood collection|/blog/at-home-blood-collection-brisbane-guide} or {LINK:book your visit|/booking} today.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // POST 3 — blood-test-cost-and-preparation-australia
  // HERO IMAGE PLACEHOLDER: glass of water and pathology request form on a bright
  // morning kitchen table — calm, early-morning fasting test at home feel.
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'blood-test-cost-and-preparation-australia',
    title: 'What Does a Blood Test Cost in Australia — and How Do I Prepare for One at Home?',
    metaTitle: 'Blood Test Cost Australia & Home Preparation Guide | Nura Health',
    metaDescription:
      'A clear breakdown of what blood tests cost in Australia across every scenario — Medicare, mobile collection, NDIS and aged care — plus a practical guide to preparing for a fasting test at home.',
    date: '2026-05-25',
    author: 'Nura Health',
    readTime: '8 min read',
    heroAlt:
      'A glass of water and a pathology request form on a kitchen table, ready for an early-morning fasting blood test at home',
    heroImage: '/images/blog/cost-preparation-hero.jpg',
    excerpt:
      'A clear breakdown of what blood tests cost in Australia across every scenario — Medicare, mobile collection, NDIS and aged care — plus a practical guide to preparing for a fasting test at home.',
    content: [
      {
        type: 'h2',
        text: 'Two questions, one answer: getting it right the first time',
      },
      {
        type: 'p',
        text: 'When a doctor hands you a pathology request form, two practical questions usually follow. What is this going to cost me? And is there anything I need to do to get ready? Both matter, because getting either wrong means wasted time — an unexpected bill, or worse, turning up to a draw that has to be repeated because you ate breakfast when you shouldn\'t have. This guide answers both clearly, with a focus on at-home collection across Brisbane, Sydney and Melbourne.',
      },
      {
        type: 'h2',
        text: 'What blood tests cost in Australia',
      },
      {
        type: 'p',
        text: 'Let\'s start with the most reassuring fact: if you have a valid request form from your doctor, the laboratory testing itself is usually bulk-billed and free under Medicare. When you walk into a collection centre, you typically pay nothing for standard tests, because the lab bills Medicare directly. This is the baseline everything else is measured against.',
      },
      {
        type: 'p',
        text: 'So where do costs come in? They come in when you want something Medicare doesn\'t cover — and the most common one is convenience. The Medicare benefit covers the test, not the travel. When a qualified collector comes to your home instead of you going to a centre, there\'s a service fee for that visit. For most patients, at-home collection sits in the range of $55 for a standard booking and around $75–$80 for a same-day or urgent request. That fee covers the collector\'s time, travel, equipment, and the safe transport of your sample to the lab — while the testing itself remains bulk-billed as usual.',
      },
      {
        type: 'p',
        text: 'A few other scenarios are worth knowing. Some tests aren\'t covered by Medicare at all — certain screening tests, some hormone or vitamin panels, and tests ordered outside standard clinical criteria — and these carry a lab fee whether collected at a centre or at home. Your doctor or the lab can tell you in advance if a specific test isn\'t covered. For NDIS participants and aged care facilities, different arrangements apply — we cover both in detail in our {LINK:NDIS and aged care guide|/blog/mobile-pathology-ndis-aged-care}.',
      },
      {
        type: 'p',
        text: 'The simple way to think about it: the test is free, the house call is the part you\'re paying for, and that fee buys back the half-morning a centre visit would have cost you.',
      },
      {
        type: 'h2',
        text: 'How to prepare for a blood test at home',
      },
      {
        type: 'p',
        text: 'Most blood tests need no special preparation at all — you simply go about your day and the collector arrives. But some tests have requirements, and the most common is fasting. Getting this right is the difference between accurate results and a repeat visit.',
      },
      {
        type: 'h2',
        text: 'Fasting tests: what "fasting" actually means',
      },
      {
        type: 'p',
        text: 'A fasting blood test requires you to avoid food and drink — other than plain water — for a set period before collection, usually between 8 and 12 hours. Common fasting tests include fasting glucose, fasting lipids/cholesterol, and some metabolic panels. Your request form or your doctor will tell you whether fasting is needed and for how long; if you\'re unsure, ask before the day rather than guessing.',
      },
      {
        type: 'p',
        text: 'Here\'s how to prepare well. In the lead-up, drink plenty of water — being well hydrated makes the draw easier and is encouraged during a fast. Plain water is fine and helps; what you must avoid is food, tea, coffee (even black), juice, milk, and chewing gum, all of which can affect results. Take your regular medications as normal unless your doctor has specifically told you otherwise — don\'t stop medication to fast without medical advice. If you take medication that must be had with food, raise it with your doctor when the test is ordered so the timing can be planned.',
      },
      {
        type: 'p',
        text: 'The single biggest advantage of fasting at home is timing. The easiest approach is to fast overnight while you sleep and book an early-morning visit, so you barely notice the fast at all — then have breakfast ready for the moment your collector leaves. There\'s no drive to the centre during which a coffee becomes irresistible, and no waiting-room delay extending your fast by another hour.',
      },
      {
        type: 'h2',
        text: 'Preparing for any home visit',
      },
      {
        type: 'p',
        text: 'Beyond fasting, a few small things make any home collection smoother. Have your pathology request form ready and to hand. Have a form of ID available so the collector can confirm your identity. Wear something with sleeves that roll up easily. Choose a spot with a comfortable chair and good light — a kitchen or dining table is ideal. And if you have a history of fainting or particularly difficult veins, mention it when you book, so your collector can plan for it. That\'s genuinely all there is to it.',
      },
      {
        type: 'h2',
        text: 'The bottom line',
      },
      {
        type: 'p',
        text: 'In Australia, the blood test itself is almost always free with Medicare; what you pay for with at-home collection is the convenience of the visit, typically $55–$80. Preparation is usually nothing at all, and where fasting is required, doing it overnight before an early home visit is the easiest version of it that exists. A little planning means accurate results the first time, with none of the hassle.',
      },
      {
        type: 'p',
        text: 'Have a request form ready? {LINK:Book your at-home collection|/booking} with Nura Health and we\'ll handle the rest.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // POST 4 — mobile-pathology-ndis-aged-care
  // HERO IMAGE PLACEHOLDER: warm, dignified shot of a collector with an elderly
  // resident in an aged care room, or with an NDIS participant in their home.
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'mobile-pathology-ndis-aged-care',
    title: 'Mobile Pathology for NDIS Participants and Aged Care Facilities',
    metaTitle: 'Mobile Pathology for NDIS & Aged Care | Nura Health',
    metaDescription:
      'For NDIS participants and aged care facilities, mobile blood collection removes far more than inconvenience — it removes access barriers, escort costs, and compliance risk. Here\'s how it works for both.',
    date: '2026-05-25',
    author: 'Nura Health',
    readTime: '9 min read',
    heroAlt:
      'Mobile phlebotomist performing a blood collection for an aged care resident in the comfort of their own room',
    heroImage: '/images/blog/ndis-aged-care-hero.jpg',
    excerpt:
      'For NDIS participants and aged care facilities, mobile blood collection removes far more than inconvenience — it removes access barriers, escort costs, and compliance risk. Here\'s how it works for both.',
    content: [
      {
        type: 'h2',
        text: 'When a blood test is more than an errand',
      },
      {
        type: 'p',
        text: 'For most people, getting to a collection centre is an inconvenience. For an NDIS participant with limited mobility, or an aged care resident who needs a staff member to accompany them off-site, it\'s something larger — a genuine barrier to care, and in the case of facilities, a recurring hidden cost and a compliance headache. Mobile pathology changes the equation for both groups in ways that go well beyond saving a trip. This guide explains how it works for NDIS participants and for aged care facilities in turn.',
      },
      {
        type: 'h2',
        text: 'Part one: NDIS participants',
      },
      {
        type: 'h2',
        text: 'The barrier that delays care',
      },
      {
        type: 'p',
        text: 'Many NDIS participants live with conditions that require regular blood monitoring — and the same conditions can make getting to a pathology centre difficult or impossible. Arranging accessible transport, coordinating a support worker\'s time, navigating an unfamiliar clinic, and managing the physical toll of the outing can turn a simple routine test into a major logistical exercise. The predictable result is that tests get postponed, and monitoring that should be routine becomes irregular. Mobile collection removes that barrier entirely: the collector comes to the participant\'s home, on the participant\'s terms, in an environment where they\'re comfortable and supported.',
      },
      {
        type: 'h2',
        text: 'Do you need to be NDIS-registered to use it?',
      },
      {
        type: 'p',
        text: 'This is a common point of confusion, so it\'s worth being precise. Participants whose plans are plan-managed or self-managed can access providers that are not NDIS-registered — which means a service like Nura Health can support these participants directly. Participants who are NDIA-managed (agency-managed) generally need to use NDIS-registered providers. If you\'re unsure which category your plan falls under, your plan manager or support coordinator can confirm it quickly.',
      },
      {
        type: 'h2',
        text: 'How it\'s funded',
      },
      {
        type: 'p',
        text: 'Where mobile collection relates to a participant\'s disability-support needs, the visit can often be funded through the NDIS plan rather than paid out of pocket. The relevant support is delivered under the plan\'s funding, and pricing is kept within the applicable NDIS price arrangements. The practical upshot is that the convenience and accessibility of a home visit doesn\'t have to come as an extra personal cost — it can be part of the support the plan already provides. A plan manager or support coordinator is the right person to confirm how a specific plan covers it. For a full breakdown of pricing across all scenarios including NDIS, see our {LINK:blood test cost and preparation guide|/blog/blood-test-cost-and-preparation-australia}.',
      },
      {
        type: 'h2',
        text: 'Why support coordinators and plan managers value it',
      },
      {
        type: 'p',
        text: 'For the coordinators and plan managers who arrange services on a participant\'s behalf, mobile pathology is an easy recommendation: it reduces the support hours spent on transport and escorting, it removes a recurring source of missed or delayed appointments, and it comes with clean documentation and straightforward invoicing. It makes a participant\'s care more reliable while reducing the administrative load around it.',
      },
      {
        type: 'h2',
        text: 'Part two: Aged care facilities',
      },
      {
        type: 'h2',
        text: 'The hidden cost no one budgets for',
      },
      {
        type: 'p',
        text: 'Here\'s a cost that rarely appears on a balance sheet but quietly drains aged care facilities every week: staff escorts for off-site appointments. When a resident has to travel to a pathology centre, a facility is generally required to send a staff member with them. That\'s one carer pulled off the floor for the duration of the trip — travel each way, waiting time at the centre, and the return — often the better part of a morning for a single blood test. Multiply that across dozens of residents needing regular monitoring, and the facility is spending a substantial number of paid care hours on transport and waiting rather than on care.',
      },
      {
        type: 'p',
        text: 'This is the core of the aged care problem, and it\'s worth stating plainly: every off-site pathology appointment consumes staff time that the facility is paying for and that residents would be better served by keeping in the building.',
      },
      {
        type: 'h2',
        text: 'Escort costs and care minute compliance',
      },
      {
        type: 'p',
        text: 'The escort issue isn\'t only financial. Aged care facilities operate under care minute requirements — obligations about the amount of direct care time delivered to residents. Every hour a staff member spends escorting one resident to an off-site appointment is an hour not spent delivering care across the facility. Off-site trips therefore create a double problem: they cost money and they pull against the very compliance metrics facilities are measured on.',
      },
      {
        type: 'p',
        text: 'Mobile pathology resolves both at once. When the collector comes to the facility, no resident leaves the building, no staff escort is required, and no care hours are lost to transport. Residents are seen in the comfort and safety of their own rooms, multiple residents can be collected in a single visit, and staff stay on the floor doing what they\'re there to do. For a Director of Nursing or facility manager, that\'s a direct reduction in cost and a direct easing of compliance pressure from the same change.',
      },
      {
        type: 'h2',
        text: 'How facility collection works',
      },
      {
        type: 'p',
        text: 'The arrangement is designed to be simple for the facility. A collector (or a dedicated collector for larger facilities) visits on a scheduled basis, working through the residents who need collection that day, confirming each resident\'s identity carefully, and handling all sample transport to the partner laboratory. Pricing for facilities is arranged on a per-resident, per-visit basis, with better rates as resident volumes increase, and larger facilities receive dedicated collectors and priority scheduling. Results flow back to each resident\'s treating doctor as normal. From the facility\'s side, it replaces a fragmented, staff-intensive process with a single reliable visit.',
      },
      {
        type: 'h2',
        text: 'The bottom line',
      },
      {
        type: 'p',
        text: 'Whether it\'s an NDIS participant for whom a centre visit is a real barrier to consistent monitoring, or an aged care facility bleeding staff hours and care minutes into off-site escorts, mobile pathology solves a problem that\'s bigger than convenience. It restores access, removes cost, and eases compliance — all by bringing the collection to the person instead of sending the person to the collection.',
      },
      {
        type: 'p',
        text: 'For plan managers, support coordinators, and facility managers: {LINK:contact Nura Health|/booking} to discuss how mobile pathology can reduce cost-to-serve and improve access for the people in your care.',
      },
    ],
  },


  // ─────────────────────────────────────────────────────────────────────────────
  // POST 5 — the-real-cost-of-a-blood-test
  // HERO IMAGE: pexels-rdne-6129141.jpg — healthcare worker walking through a
  // clinical waiting room with empty chairs.
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'the-real-cost-of-a-blood-test',
    title: 'The Real Cost of a Blood Test: Why Australians Are Losing More Than Just Blood at Collection Centres',
    metaTitle: 'The Real Cost of a Blood Test | Nura Health Blog',
    metaDescription: 'A blood test costs you more than a bandaid. The average Australian loses 1.5 to 2 hours and $50 to $75 of productivity per pathology visit. Here is the breakdown.',
    date: '2026-06-26',
    author: 'Rishi Bhardwaj',
    readTime: '11 min read',
    heroAlt: 'Healthcare worker walking through a clinical waiting room with empty chairs',
    heroImage: '/images/blog/pexels-rdne-6129141.jpg',
    excerpt: 'A blood test should cost you five minutes and a small bandaid. Not two hours and $75 of lost productivity. Here is the real breakdown of what Australians are losing at collection centres.',
    content: [
      {
        type: 'p',
        text: 'You get a pathology request from your GP. Simple enough. Drive to the collection centre, take a number, roll up your sleeve, done. Except it is never that simple.',
      },
      {
        type: 'p',
        text: 'What should be a 10-minute procedure quietly eats 90 minutes of your day, costs you real money you never see on a receipt, and has a 1-in-5 chance of going wrong enough that you have to do it all over again.',
      },
      {
        type: 'p',
        text: 'Let me walk through the real numbers.',
      },
      {
        type: 'h2',
        text: 'The time nobody talks about',
      },
      {
        type: 'p',
        text: 'The blood draw itself takes about 5 to 10 minutes. That part is fine.',
      },
      {
        type: 'p',
        text: 'Everything around it is the problem.',
      },
      {
        type: 'p',
        text: 'The average Australian in a capital city spends about 66 minutes a day commuting to and from work. A pathology centre is rarely next door to your house or your office. For most people, the round trip to a collection centre adds 20 to 40 minutes of travel, depending on whether you are driving in peak hour traffic, finding parking, or catching public transport.',
      },
      {
        type: 'p',
        text: "Then there is the wait. Pathology centres are notoriously busy in the mornings, which is precisely when most people need to go because of fasting requirements. Even Douglass Hanly Moir, one of Australia's largest providers, acknowledges on their website that mornings are their busiest period and suggests patients attend after midday \"where possible.\" But if you are fasting for a cholesterol panel or glucose test, midday is not an option.",
      },
      {
        type: 'p',
        text: 'Our own research, based on conversations with patients and collectors across Brisbane, Sydney and Melbourne, puts the average wait time at 45 minutes or more during peak hours. Some centres regularly push past an hour.',
      },
      {
        type: 'p',
        text: 'Add it up: 20 to 40 minutes of travel, 45 minutes of waiting, 10 minutes for the draw, and the return trip. A conservative estimate puts the total time commitment at 1.5 to 2 hours per visit.',
      },
      {
        type: 'cta',
        heading: 'Skip the waiting room entirely.',
        body: 'Nura Health sends a qualified collector to your door. Same pathology request, same accredited lab, zero travel time.',
        buttonText: 'Book a Home Collection',
        buttonHref: '/booking',
      },
      {
        type: 'h2',
        text: 'Now put a dollar figure on it',
      },
      {
        type: 'p',
        text: 'The median full-time salary in Australia is approximately $90,500 per year, based on August 2025 ABS data. That works out to roughly $47 per hour.',
      },
      {
        type: 'p',
        text: 'In metro areas like Sydney, Melbourne, and Brisbane, where median earnings skew higher and where collection centre congestion is worst, the effective hourly rate is closer to $50 to $60.',
      },
      {
        type: 'p',
        text: 'So that 1.5-hour blood test visit is not free, even when the pathology itself is bulk-billed. You are spending $50 to $90 worth of your time. Whether you take annual leave, go unpaid, or sacrifice a Saturday morning that could have been spent with family, you are paying for it.',
      },
      {
        type: 'p',
        text: 'For someone on a $100,000 salary in an urban metro, the opportunity cost of a single visit sits comfortably between $50 and $75. That is the hidden price tag that never appears on a Medicare statement.',
      },
      {
        type: 'h2',
        text: 'The recollection problem makes it worse',
      },
      {
        type: 'p',
        text: 'Here is the part that really stings.',
      },
      {
        type: 'p',
        text: 'Not every blood test gets it right the first time. Pre-analytical errors, the ones that happen before your sample even reaches a lab analyser, account for 46% to 68% of all laboratory errors according to published clinical research. These include wrong tubes, insufficient blood volume, clotted samples, mislabelled specimens, and handling issues during transport.',
      },
      {
        type: 'p',
        text: 'The pooled global prevalence of blood specimen rejection sits at roughly 2%, according to a 2022 systematic review and meta-analysis published in PMC. Some studies report rejection rates as high as 5%.',
      },
      {
        type: 'p',
        text: 'The Australian Government Department of Health states that about 2% of all pathology specimens have a minor issue that may require clarification and delay the result. And that is the conservative figure for what gets flagged. Many pre-analytical errors, such as a tourniquet left on too long or a sample not processed within the right window, affect results without ever triggering a formal rejection.',
      },
      {
        type: 'p',
        text: 'When a sample is rejected or results are inconclusive, you get a call asking you to come back and do it all over again. Same drive. Same wait. Same lost time.',
      },
      {
        type: 'p',
        text: 'If even 1 in 10 to 1 in 5 visits results in a need for recollection (factoring in both formal rejections and the broader category of pre-analytical issues that compromise results), the annualised cost of pathology visits for a regularly tested Australian is far higher than anyone realises.',
      },
      {
        type: 'cta',
        heading: 'Tired of repeat visits?',
        body: 'Our NPAAC-certified collectors get it right the first time, with proper specimen handling and temperature-controlled transport direct to the lab.',
        buttonText: 'Book Now',
        buttonHref: '/booking',
      },
      {
        type: 'h2',
        text: 'Scale it up and the numbers are staggering',
      },
      {
        type: 'p',
        text: 'Australia conducts close to 500 million pathology tests every year. More than 11 million Australians have at least one test annually. The volume per capita is climbing too, up from 366 tests per 100 people a decade ago to 438 tests per 100 people in 2022-23.',
      },
      {
        type: 'p',
        text: 'Even if only a fraction of those tests require a physical collection centre visit, the aggregate time lost across the workforce is enormous. Millions of hours of productivity, quietly draining out through waiting rooms in strip malls across the country.',
      },
      {
        type: 'h2',
        text: 'Who gets hit hardest',
      },
      {
        type: 'p',
        text: 'This is not just an inconvenience for office workers. The time and cost burden falls disproportionately on people who can least afford it.',
      },
      {
        type: 'p',
        text: 'Shift workers who cannot leave mid-shift without losing pay. Parents managing school drop-offs who have a narrow window to get to a centre before it gets busy. Elderly Australians who may need a carer or family member to drive them, doubling the time cost. People with chronic conditions like diabetes or thyroid disorders who need blood work every 3 to 6 months. NDIS participants with mobility challenges for whom a trip to a collection centre can require a support worker, transport funding, and hours of planning.',
      },
      {
        type: 'p',
        text: 'For these groups, the true cost of a single blood test is not $50. It can easily exceed $100 to $150 when you factor in support worker hours, transport, lost wages for carers, and the cascading schedule disruptions.',
      },
      {
        type: 'cta',
        heading: 'We come to you.',
        body: 'Whether you are at home, at work, or in an aged care facility. Available across Brisbane, Sydney, and Melbourne.',
        buttonText: 'Book Your Collection',
        buttonHref: '/booking',
      },
      {
        type: 'h2',
        text: 'There is a better way',
      },
      {
        type: 'p',
        text: 'This is exactly why we started Nura Health.',
      },
      {
        type: 'p',
        text: 'We send qualified, police-checked phlebotomists to your home, your workplace, or your aged care facility. Same pathology request from your GP. Same accredited lab processing your results. Same bulk-billed Medicare claim for the test itself. The only difference is you do not lose your morning to a waiting room.',
      },
      {
        type: 'p',
        text: 'The collection happens at your kitchen table, or in the break room at work, or in a resident\'s room at their aged care facility. No driving. No parking. No waiting. No taking a number.',
      },
      {
        type: 'p',
        text: 'For aged care facilities, we eliminate the hidden compliance cost of sending staff to escort residents off-site for blood tests. For NDIS participants, we remove the transport and support barriers entirely. For busy professionals, we give you back 90 minutes of your day.',
      },
      {
        type: 'h2',
        text: 'The bottom line',
      },
      {
        type: 'p',
        text: 'A blood test should cost you five minutes and a small bandaid. Not two hours and $75 of lost productivity.',
      },
      {
        type: 'p',
        text: 'The pathology system in Australia is world-class when it comes to the science. The collection experience has not kept up. It is still built around the assumption that patients will come to the lab, wait their turn, and absorb the hidden costs without complaint.',
      },
      {
        type: 'p',
        text: 'That model worked when there were fewer tests, shorter wait times, and a workforce that mostly clocked in and out at the same office every day. None of those things are true anymore.',
      },
      {
        type: 'p',
        text: 'It is time the collection experience caught up with the rest of modern healthcare.',
      },
      {
        type: 'cta',
        heading: 'Ready to skip the queue?',
        body: 'Book a home collection with Nura Health. From $55. Available 7 days.',
        buttonText: 'Book a Home Collection',
        buttonHref: '/booking',
        phone: '0420 424 290',
      },
      {
        type: 'h2',
        text: 'Sources',
      },
      {
        type: 'sources',
        items: [
          'Australian Bureau of Statistics, Employee Earnings August 2025 and Average Weekly Earnings November 2025',
          'Australian Institute of Health and Welfare, Pathology and Diagnostic Services reporting',
          'PMC systematic review: Blood specimen rejection rate in clinical laboratory (2022)',
          'Australian Government Department of Health, Pathology: The Facts',
          'MLO Online: Recollecting Blood Specimens (pre-analytical error rates)',
          'Real Insurance, Real Australian Commute Report 2025',
          'HILDA Survey, commuting time data (University of Melbourne)',
          'Douglass Hanly Moir Pathology, Patient FAQs',
        ],
      },
    ],
  },

];
